// @ts-check
// Qué se le dice al alumno sobre la puntuación de su intento.
//
// ## Por qué está aquí y no en cada panel
//
// La frase estaba escrita TRES veces, palabra por palabra, en los paneles de
// corrección de los tres editores (`diagram-editor/autocorrection-panel.js`,
// `pseudocode-editor/attempt-panel.js`, `python-editor/attempt-panel.js`).
// Es copia visible: si una de las tres cambiara, dos alumnos con la misma nota
// leerían explicaciones distintas según el editor en el que estuvieran.
//
// Al añadir el descuento por REPETIR (2026-08-07) habría hecho falta tocar las
// tres a mano. Esta es la ocasión de que dejen de poder separarse.
//
// Es una función PURA: recibe números y devuelve una frase. No toca el DOM ni
// sabe de qué editor viene.

/**
 * @typedef {{
 *   correcto?: boolean,
 *   puntuacionObtenida?: number|null,
 *   penalizacionPista?: number|null,
 *   penalizacionReintento?: number|null,
 * }} DatosPuntuacion
 */

/**
 * Cómo empieza la frase, justo antes de la cifra. Exportado para el rediseño
 * (Fase 6b): los XP del resultado cuentan hasta su cifra
 * (`components/base/momentos.js`), que encuentra la frase por aquí.
 */
export const PREFIJO_PUNTUACION = 'Puntuación obtenida en este intento: ';

/**
 * @param {unknown} valor
 * @returns {number}
 */
function descuento(valor) {
  return typeof valor === 'number' && valor > 0 ? valor : 0;
}

/**
 * Frase completa sobre la puntuación de este intento, o `null` si no hay nada
 * que decir (el ejercicio no otorga XP: `puntuacionObtenida` no es un número).
 *
 * Los dos descuentos se nombran POR SEPARADO cuando ambos se aplican. Con un
 * solo número el alumno no sabría si le costó la pista o repetir, que es
 * justo lo que tiene que aprender a distinguir.
 *
 * @param {DatosPuntuacion} datos
 * @returns {string|null}
 */
export function mensajeDePuntuacion(datos) {
  if (typeof datos.puntuacionObtenida !== 'number') return null;

  const base = `${PREFIJO_PUNTUACION}${datos.puntuacionObtenida} XP`;

  // Un intento fallido devuelve 0 del servidor: la frase acompaña al veredicto
  // en vez de contradecirlo, y no menciona descuentos que no vienen a cuento.
  if (datos.correcto === false) {
    return `${base}. Cuando la solución sea correcta, sumarás la puntuación del ejercicio.`;
  }

  const porPista = descuento(datos.penalizacionPista);
  const porRepetir = descuento(datos.penalizacionReintento);

  /** @type {string[]} */
  const motivos = [];
  if (porPista > 0) motivos.push(`${porPista} XP por pedir la pista`);
  if (porRepetir > 0) motivos.push(`${porRepetir} XP por repetir el ejercicio`);

  if (motivos.length === 0) return `${base}.`;
  return `${base} (se han descontado ${motivos.join(' y ')}).`;
}
